/**
 * The address bar is the state.
 *
 * Every place in this surface has a URL, and the browser's back button walks the way you
 * came. Before this, navigation lived in React state: reload landed you on the home page,
 * back left the application entirely, and a link to "the product board" could not be sent
 * to anybody — including to yourself, on the phone.
 *
 * **Hash routing, not history routing.** The surface is a static bundle behind nginx; a
 * real path like `/at/docs/product` would have to be rewritten to `index.html` by the
 * server, and a deploy that forgets the rewrite gives a 404 to anyone who reloads a deep
 * page. A hash never reaches the server, so the routing cannot be broken by the server.
 *
 *     #/<project>/                     the project's own page
 *     #/<project>/lane/<id>            one category
 *     #/<project>/at/<dir>             a section, or a board
 *     #/<project>/at/<dir>?v=catalog   a board, in a named view
 *     #/<project>/read/<path>          one document
 *
 * The project is always in the address. Leaving it out would make a shared link mean
 * different things to two people, or to the same person after switching project.
 */
export type Where =
  | { at: "home" }
  | { at: "category"; id: string }
  | { at: "dir"; path: string }
  | { at: "doc"; path: string }

export interface Route {
  project: string
  where: Where
  /** Which view of a board. Empty is the board's default. */
  view: string
}

export const HOME: Where = { at: "home" }

export function parse(hash: string): Route {
  const raw = hash.replace(/^#\/?/, "")
  const [pathPart, queryPart] = raw.split("?")
  const view = new URLSearchParams(queryPart ?? "").get("v") ?? ""
  const parts = pathPart.split("/").filter(Boolean).map(decodeURIComponent)

  const project = parts[0] ?? ""
  const kind = parts[1] ?? ""
  const rest = parts.slice(2).join("/")

  if (kind === "lane" && rest) return { project, view, where: { at: "category", id: rest } }
  if (kind === "at" && rest) return { project, view, where: { at: "dir", path: rest } }
  if (kind === "read" && rest) return { project, view, where: { at: "doc", path: rest } }
  return { project, view, where: HOME }
}

export function href(route: Route): string {
  const p = encodeURIComponent(route.project)
  const enc = (s: string) => s.split("/").map(encodeURIComponent).join("/")
  const q = route.view ? `?v=${encodeURIComponent(route.view)}` : ""
  switch (route.where.at) {
    case "category": return `#/${p}/lane/${enc(route.where.id)}${q}`
    case "dir": return `#/${p}/at/${enc(route.where.path)}${q}`
    case "doc": return `#/${p}/read/${enc(route.where.path)}${q}`
    default: return `#/${p}/`
  }
}

/**
 * Go somewhere. Setting the hash is what makes the browser record a step, so this is the
 * only way anything in the surface navigates — a component that changed state directly
 * would be a page the back button cannot return from.
 */
export function go(route: Route): void {
  const next = href(route)
  if (location.hash !== next) location.hash = next
}

/** Replace without recording a step — for filling in a project the address omitted. */
export function replace(route: Route): void {
  history.replaceState(null, "", href(route))
}

export function sameWhere(a: Where, b: Where): boolean {
  if (a.at !== b.at) return false
  if (a.at === "category" && b.at === "category") return a.id === b.id
  if (a.at === "dir" && b.at === "dir") return a.path === b.path
  if (a.at === "doc" && b.at === "doc") return a.path === b.path
  return true
}
